/*
  hh-lightbox.js
  ------------------------------------------------------------------
  Click any photograph on the site to see it full size.

  Why this exists: the archive pages serve images at 900px so the grids stay
  fast, but several of the originals are 2000 to 2600px wide and the detail is
  the point. This opens the original, in place, without leaving the page.

  How it picks images: everything inside <main> that is not already wrapped in
  a link (those should follow the link), not a logo or a decorative mark, and
  not smaller than a thumbnail. Header and footer are outside <main>, so the
  logo is never caught.

  It has no dependencies and injects its own styles, so the only wiring needed
  is the one script tag in sections/hh-footer.liquid.
*/
(function () {
  'use strict';

  var MIN_WIDTH = 220; // below this it is an icon, not a photograph
  var items = [];
  var index = 0;
  var lastFocus = null;
  var box, imgEl, capEl, countEl, prevBtn, nextBtn, closeBtn;

  /* ---------- styles ---------- */
  var CSS = [
    '.hhlb-zoom{cursor:zoom-in}',
    '.hhlb{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;',
    'background:rgba(12,8,4,0.94);padding:2.4rem;opacity:0;transition:opacity .18s ease}',
    '.hhlb[hidden]{display:none}',
    '.hhlb.is-open{opacity:1}',
    '.hhlb__fig{margin:0;display:flex;flex-direction:column;align-items:center;gap:1.6rem;max-width:100%;max-height:100%}',
    '.hhlb__img{max-width:min(100%,1400px);max-height:78vh;width:auto;height:auto;display:block;',
    'box-shadow:0 2.4rem 6rem rgba(0,0,0,.6);opacity:0;transition:opacity .2s ease}',
    '.hhlb__img.is-loaded{opacity:1}',
    '.hhlb__cap{display:flex;flex-direction:column;align-items:center;gap:.6rem;text-align:center;max-width:78ch}',
    '.hhlb__caption{font-family:var(--hh-text,Georgia,serif);font-size:1.5rem;line-height:1.5;color:#f4ead6}',
    '.hhlb__count{font-family:var(--hh-text,Georgia,serif);font-size:1.15rem;letter-spacing:.16em;',
    'text-transform:uppercase;color:var(--hh-amber,#c9922f)}',
    '.hhlb button{position:absolute;background:transparent;border:0;color:#f4ead6;cursor:pointer;',
    'line-height:1;padding:1.2rem;min-width:4.8rem;min-height:4.8rem;opacity:.72;transition:opacity .15s ease}',
    '.hhlb button:hover,.hhlb button:focus-visible{opacity:1}',
    '.hhlb button:focus-visible{outline:2px solid var(--hh-amber,#c9922f);outline-offset:2px}',
    '.hhlb__close{top:1.2rem;right:1.6rem;font-size:3.2rem}',
    '.hhlb__nav{top:50%;transform:translateY(-50%);font-size:4.4rem}',
    '.hhlb__nav--prev{left:1rem}.hhlb__nav--next{right:1rem}',
    '.hhlb__nav[disabled]{opacity:.18;cursor:default}',
    'html.hhlb-open{overflow:hidden}',
    '@media screen and (max-width:749px){',
    '.hhlb{padding:1.2rem}.hhlb__img{max-height:66vh}',
    '.hhlb__nav{font-size:3.4rem}.hhlb__nav--prev{left:0}.hhlb__nav--next{right:0}',
    '.hhlb__caption{font-size:1.35rem}}',
    '@media (prefers-reduced-motion:reduce){.hhlb,.hhlb__img{transition:none}}'
  ].join('');

  function injectCss() {
    var s = document.createElement('style');
    s.setAttribute('data-hh-lightbox', '');
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ---------- helpers ---------- */

  // Strip the resize parameters so the CDN hands back the original.
  function fullSrc(img) {
    var src = img.getAttribute('src') || '';
    if (!src) return '';
    try {
      var u = new URL(src, window.location.href);
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.href;
    } catch (e) {
      return src.split(/[?&]width=/)[0];
    }
  }

  // Archive captions are two spans, the caption and the year. Joined with a
  // comma they read as one line; textContent alone would run them together.
  function captionFor(img) {
    var fig = img.closest('figure');
    var cap = fig && fig.querySelector('figcaption');
    if (cap) {
      var spans = cap.querySelectorAll('span');
      if (spans.length) {
        var parts = [];
        Array.prototype.forEach.call(spans, function (sp) {
          var t = sp.textContent.trim();
          if (t) parts.push(t);
        });
        if (parts.length) return parts.join(', ');
      }
      var t2 = cap.textContent.replace(/\s+/g, ' ').trim();
      if (t2) return t2;
    }
    return (img.getAttribute('alt') || '').trim();
  }

  function eligible(img) {
    if (img.closest('a')) return false;                 // let the link win
    if (img.hasAttribute('data-no-zoom')) return false;
    if (img.closest('[data-no-zoom]')) return false;
    var src = img.getAttribute('src') || '';
    if (!src || src.indexOf('.svg') > -1) return false; // marks and logos
    var w = img.naturalWidth || img.width || parseInt(img.getAttribute('width'), 10) || 0;
    // Only fall back to the rendered box when the real width is unknown: a lazy
    // image that has not decoded yet reports 0, and measuring it before layout
    // was what left the lyric sheet out of the viewer.
    if (w) { if (w < MIN_WIDTH) return false; }
    else if (img.getBoundingClientRect().width < 100) return false;
    return true;
  }

  /* ---------- overlay ---------- */

  function build() {
    box = document.createElement('div');
    box.className = 'hhlb';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Photograph viewer');
    box.hidden = true;
    box.innerHTML =
      '<button class="hhlb__close" type="button" aria-label="Close">&times;</button>' +
      '<button class="hhlb__nav hhlb__nav--prev" type="button" aria-label="Previous photograph">&#8249;</button>' +
      '<button class="hhlb__nav hhlb__nav--next" type="button" aria-label="Next photograph">&#8250;</button>' +
      '<figure class="hhlb__fig">' +
      '<img class="hhlb__img" alt="">' +
      '<figcaption class="hhlb__cap"><span class="hhlb__caption"></span><span class="hhlb__count"></span></figcaption>' +
      '</figure>';
    document.body.appendChild(box);

    imgEl = box.querySelector('.hhlb__img');
    capEl = box.querySelector('.hhlb__caption');
    countEl = box.querySelector('.hhlb__count');
    prevBtn = box.querySelector('.hhlb__nav--prev');
    nextBtn = box.querySelector('.hhlb__nav--next');
    closeBtn = box.querySelector('.hhlb__close');

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { step(-1); });
    nextBtn.addEventListener('click', function () { step(1); });
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target.classList.contains('hhlb__fig')) close();
    });
    imgEl.addEventListener('load', function () { imgEl.classList.add('is-loaded'); });

    var x0 = null;
    box.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
      x0 = null;
    }, { passive: true });
  }

  function show(i) {
    index = (i + items.length) % items.length;
    var img = items[index];
    imgEl.classList.remove('is-loaded');
    imgEl.src = fullSrc(img);
    imgEl.alt = img.getAttribute('alt') || '';
    capEl.textContent = captionFor(img);
    countEl.textContent = items.length > 1 ? (index + 1) + ' of ' + items.length : '';
    prevBtn.disabled = nextBtn.disabled = items.length < 2;
    // warm the neighbours so stepping is instant
    [items[(index + 1) % items.length], items[(index - 1 + items.length) % items.length]]
      .forEach(function (n) { if (n) { var p = new Image(); p.src = fullSrc(n); } });
  }

  function step(d) { if (items.length > 1) show(index + d); }

  function open(i) {
    lastFocus = document.activeElement;
    box.hidden = false;
    document.documentElement.classList.add('hhlb-open');
    show(i);
    requestAnimationFrame(function () { box.classList.add('is-open'); });
    closeBtn.focus();
    document.addEventListener('keydown', onKey);
  }

  function close() {
    box.classList.remove('is-open');
    document.documentElement.classList.remove('hhlb-open');
    document.removeEventListener('keydown', onKey);
    window.setTimeout(function () { box.hidden = true; imgEl.removeAttribute('src'); }, 180);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(); }
    else if (e.key === 'ArrowRight') { step(1); }
    else if (e.key === 'ArrowLeft') { step(-1); }
    else if (e.key === 'Tab') { e.preventDefault(); closeBtn.focus(); }
  }

  /* ---------- wiring ---------- */

  function collect() {
    var main = document.querySelector('main') || document.body;
    var found = [];
    Array.prototype.forEach.call(main.querySelectorAll('img'), function (img) {
      if (!eligible(img)) return;
      found.push(img);
      if (!img.classList.contains('hhlb-zoom')) {
        img.classList.add('hhlb-zoom');
        img.setAttribute('tabindex', '0');
        img.setAttribute('role', 'button');
        var a = (img.getAttribute('alt') || 'photograph').trim();
        img.setAttribute('aria-label', 'View full size: ' + a);
        img.addEventListener('click', function () { open(items.indexOf(img)); });
        img.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(items.indexOf(img)); }
        });
      }
    });
    items = found;
  }

  function init() {
    injectCss();
    build();
    collect();
    // Lazy loaded images report naturalWidth only once decoded, so sweep again
    // when they land and when sections are re-rendered in the theme editor.
    window.addEventListener('load', collect);
    document.addEventListener('shopify:section:load', collect);
    // img load events do not bubble, so catch them on the way down. This picks
    // up every lazy loaded photograph the moment it decodes.
    document.addEventListener('load', function (e) {
      if (e.target && e.target.tagName === 'IMG') collect();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();